import { useState } from "react";
import { HelpCircle, Loader2 } from "lucide-react";

// Vendor identity ambiguity → the operator decides. Never shows technical/database wording.
const Choice = ({ c, disabled, onUse }) => (
  <li className="flex items-center justify-between gap-3 rounded-md border border-[#E4E0D8] bg-white px-3 py-2" data-testid={`vendor-candidate-${c.vendor_id}`}>
    <div className="min-w-0">
      <p className="text-[15px] font-medium text-archival-primary break-words">{c.vendor_name}</p>
      <p className="text-[12px] font-mono text-archival-secondary">{c.gstin || "No GSTIN on file"}</p>
      {c.gstin_relation === "NEAR" && <p className="text-[12px] text-amber-800">GSTIN is very close to the one on the bill</p>}
      {c.previously_confirmed > 0 && <p className="text-[12px] text-archival-muted">You've matched this vendor before</p>}
    </div>
    <button type="button" disabled={disabled} onClick={() => onUse(c.vendor_id)} data-testid={`vendor-use-${c.vendor_id}`}
      className="h-11 px-3 shrink-0 rounded-md text-white text-[14px] font-semibold disabled:opacity-60" style={{ backgroundColor: "var(--action)" }}>
      Use {c.vendor_name}
    </button>
  </li>
);

const CorrectForm = ({ ev, disabled, onSave }) => {
  const [name, setName] = useState(ev.vendor_name || "");
  const [gstin, setGstin] = useState(ev.gstin || "");
  return (
    <form className="space-y-2" data-testid="vendor-correct-form" onSubmit={(e) => { e.preventDefault(); onSave({ vendor_name: name, gstin }); }}>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Vendor name" data-testid="vendor-correct-name"
        className="w-full h-11 rounded-md border border-[#E4E0D8] px-3 text-[15px]" />
      <input value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())} placeholder="GSTIN" data-testid="vendor-correct-gstin"
        className="w-full h-11 rounded-md border border-[#E4E0D8] px-3 text-[15px] font-mono" />
      <button type="submit" disabled={disabled} data-testid="vendor-correct-save"
        className="h-11 px-4 rounded-md border border-[#E4E0D8] bg-white text-[14px] font-semibold">Save details</button>
    </form>
  );
};

export const VendorResolutionPanel = ({ identity, canEdit, busy, error, onDecide }) => {
  const [correcting, setCorrecting] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const ev = identity.evidence || {};
  const cands = identity.candidates || [];
  const malformed = (identity.reasons || []).includes("GSTIN_MALFORMED");
  const title = cands.length ? (cands.length === 1 ? "We found a possible vendor" : "This vendor may already exist. Please choose.")
    : malformed ? "Please check the vendor's GSTIN" : "Please confirm the vendor";
  return (
    <section role="alert" data-testid="vendor-resolution-panel" className="rounded-md border-2 border-amber-300 bg-amber-50 p-4 space-y-3">
      <div className="flex gap-2 items-start">
        <HelpCircle size={20} className="text-amber-800 shrink-0 mt-0.5" aria-hidden />
        <div className="min-w-0">
          <h2 className="text-[16px] font-semibold text-amber-900" data-testid="vendor-resolution-title">{title}</h2>
          <p className="text-[14px] text-amber-900 break-words" data-testid="vendor-evidence-name">{ev.vendor_name || "No vendor name"}</p>
          <p className="text-[13px] text-amber-900/80">GSTIN read from bill: <span className="font-mono" data-testid="vendor-evidence-gstin">{ev.gstin || "none"}</span></p>
          {malformed && <p className="text-[13px] text-amber-900/80" data-testid="vendor-gstin-malformed">This GSTIN doesn't look complete or valid.</p>}
        </div>
      </div>
      {cands.length > 0 && (
        <ul className="space-y-2" data-testid="vendor-candidates">
          {(showAll ? cands : cands.slice(0, 3)).map((c) => <Choice key={c.vendor_id} c={c} disabled={!canEdit || busy} onUse={(id) => onDecide({ decision: "BIND_EXISTING", vendor_id: id })} />)}
        </ul>
      )}
      {cands.length > 3 && !showAll && (
        <button type="button" onClick={() => setShowAll(true)} data-testid="vendor-show-more"
          className="min-h-[44px] text-[14px] text-amber-900 underline underline-offset-2">Show {cands.length - 3} more possible vendors</button>
      )}
      {canEdit ? (
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={busy} onClick={() => onDecide({ decision: "CREATE_NEW" })} data-testid="vendor-create-new"
            className="h-11 px-4 rounded-md border-2 border-amber-400 bg-white text-[14px] font-semibold text-amber-900">Create as new vendor</button>
          <button type="button" disabled={busy} onClick={() => setCorrecting((v) => !v)} data-testid="vendor-correct-toggle"
            className="h-11 px-4 rounded-md border border-[#E4E0D8] bg-white text-[14px]">Correct bill details</button>
          {busy && <Loader2 size={16} className="animate-spin self-center" aria-hidden />}
        </div>
      ) : <p className="text-[13px] text-amber-900" data-testid="vendor-no-permission">Someone who can confirm bills needs to choose the vendor.</p>}
      {canEdit && correcting && <CorrectForm ev={ev} disabled={busy} onSave={(f) => { setCorrecting(false); onDecide({ decision: "CORRECT_SOURCE", ...f }); }} />}
      {error && <p className="text-[13px] text-red-700" data-testid="vendor-resolution-error">{error}</p>}
    </section>
  );
};
